import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Paper, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import FunnelBreakdownSkeleton from './skeletons/FunnelBreakdownSkeleton';

const FunnelBreakdownSection = (props) => {
  const {
    isLoading,
    errorMessage,
    stageMissingDates,
    stageRows,
    stageDistributionMax,
  } = props;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          lineHeight: '14px',
          letterSpacing: '0.04em',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression='metrics.LeadFunnel.FunnelBreakdownSection' />
      </Typography>
      {
        isLoading &&
        <FunnelBreakdownSkeleton />
      }
      {
        !isLoading &&
        errorMessage &&
        <Alert severity='warning'>
          {errorMessage}
        </Alert>
      }
      {
        !isLoading &&
        !errorMessage &&
        <Stack spacing={2}>
          {
            stageMissingDates.length > 0 &&
            <Alert severity='info'>
              <Translate
                expression='metrics.LeadFunnel.ClientStageMetrics.MissingSnapshot'
                substitute={{dates: stageMissingDates.join(', ')}}
              />
            </Alert>
          }
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))'},
              gap: 2,
            }}
          >
            {/* Stage distribution card */}
            <Paper
              sx={{
                p: 3,
                borderRadius: '12px',
                border: '1px solid #E5E5E5',
                boxShadow: 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '21px',
                  color: '#1A1916',
                  mb: 0.75,
                }}
              >
                <Translate expression='metrics.LeadFunnel.FunnelBreakdown.StageDistribution' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#A8A49E',
                  mb: 3,
                }}
              >
                <Translate expression='metrics.LeadFunnel.FunnelBreakdown.StageDistributionSubtitle' />
              </Typography>
              <Stack spacing={2}>
                {
                  stageRows
                      .filter((stage) => stage.key !== 'CLOSED_LOST')
                      .map((stage) => {
                        const barWidth = stage.nowCount === null ? 0 : `${(stage.nowCount / stageDistributionMax) * 100}%`;

                        return (
                          <Stack key={stage.key} direction='row' alignItems='center' spacing={1.25}>
                            <Typography
                              sx={{
                                width: 110,
                                fontSize: '11px',
                                lineHeight: '14px',
                                color: '#919191',
                                textAlign: 'right',
                                flexShrink: 0,
                              }}
                            >
                              {stage.label}
                            </Typography>
                            <Box
                              sx={{
                                flex: 1,
                                height: 8,
                                backgroundColor: '#F0F0F0',
                                borderRadius: '4px',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  width: barWidth,
                                  minWidth: stage.nowCount ? '10px' : 0,
                                  height: '100%',
                                  backgroundColor: stage.lineColor,
                                  borderRadius: '4px',
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                width: 24,
                                fontSize: '11px',
                                lineHeight: '14px',
                                color: '#919191',
                                textAlign: 'right',
                                flexShrink: 0,
                              }}
                            >
                              {stage.nowCount === null ? '--' : stage.nowCount}
                            </Typography>
                          </Stack>
                        );
                      })
                }
              </Stack>
            </Paper>

            {/* Stage detail card */}
            <Paper
              sx={{
                p: 3,
                borderRadius: '12px',
                border: '1px solid #E5E5E5',
                boxShadow: 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: '21px',
                  color: '#1A1916',
                  mb: 0.75,
                }}
              >
                <Translate expression='metrics.LeadFunnel.FunnelBreakdown.StageDetail' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: '#A8A49E',
                  mb: 2.5,
                }}
              >
                <Translate expression='metrics.LeadFunnel.FunnelBreakdown.StageDetailSubtitle' />
              </Typography>
              <Stack direction='row' alignItems='center' sx={{pb: 1.5, borderBottom: '1px solid #EAEAEA'}}>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '14px',
                    letterSpacing: '0.04em',
                    color: '#A8A49E',
                  }}
                >
                  <Translate expression='metrics.LeadFunnel.FunnelBreakdown.Stage' />
                </Typography>
                <Typography
                  sx={{
                    width: 56,
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '14px',
                    letterSpacing: '0.04em',
                    color: '#A8A49E',
                    textAlign: 'right',
                  }}
                >
                  <Translate expression='metrics.LeadFunnel.FunnelBreakdown.Now' />
                </Typography>
                <Typography
                  sx={{
                    width: 56,
                    ml: 2,
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '14px',
                    letterSpacing: '0.04em',
                    color: '#A8A49E',
                    textAlign: 'right',
                  }}
                >
                  <Translate expression='metrics.LeadFunnel.FunnelBreakdown.Prev' />
                </Typography>
                <Typography
                  sx={{
                    width: 56,
                    ml: 2,
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '14px',
                    letterSpacing: '0.04em',
                    color: '#A8A49E',
                    textAlign: 'right',
                  }}
                >
                  <Translate expression='metrics.LeadFunnel.FunnelBreakdown.Delta' />
                </Typography>
              </Stack>
              {
                stageRows.map((stage, index) => {
                  let deltaLabel = '--';

                  if (stage.delta !== null) {
                    deltaLabel = stage.delta > 0 ? `+${stage.delta}` : `${stage.delta}`;
                  }

                  return (
                    <Stack
                      key={stage.key}
                      direction='row'
                      alignItems='center'
                      sx={{
                        py: 1.5,
                        px: 1,
                        borderRadius: '2px',
                        backgroundColor: stage.rowBackgroundColor,
                        borderBottom: index === stageRows.length - 1 ? 'none' : '1px solid #EAEAEA',
                      }}
                    >
                      <Stack direction='row' alignItems='center' spacing={1} sx={{flex: 1, minWidth: 0}}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: stage.dotColor,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '11px',
                            lineHeight: '14px',
                            color: '#000000',
                          }}
                        >
                          {stage.label}
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          width: 56,
                          fontSize: '11px',
                          lineHeight: '14px',
                          color: '#000000',
                          textAlign: 'right',
                        }}
                      >
                        {stage.nowCount === null ? '--' : stage.nowCount}
                      </Typography>
                      <Typography
                        sx={{
                          width: 56,
                          ml: 2,
                          fontSize: '11px',
                          lineHeight: '14px',
                          color: '#000000',
                          textAlign: 'right',
                        }}
                      >
                        {stage.prevCount === null ? '--' : stage.prevCount}
                      </Typography>
                      <Typography
                        sx={{
                          width: 56,
                          ml: 2,
                          fontSize: '11px',
                          lineHeight: '14px',
                          color: stage.delta > 0 ? '#21892C' : stage.delta < 0 ? '#DA7955' : '#000000',
                          textAlign: 'right',
                        }}
                      >
                        {deltaLabel}
                      </Typography>
                    </Stack>
                  );
                })
              }

            </Paper>
          </Box>
        </Stack>
      }
    </Box>
  );
};

FunnelBreakdownSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  stageMissingDates: PropTypes.array.isRequired,
  stageRows: PropTypes.array.isRequired,
  stageDistributionMax: PropTypes.number.isRequired,
};

export default React.memo(FunnelBreakdownSection);
